import { showInfoModal } from '@/components/common/InfoModal';
import { useMutation, useQuery } from '@apollo/client';
import { ListItem, UnorderedList } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    activeTransactionSchedulesFromGoalAccount,
    PlentyGoalAccountFinancialEntity,
} from '@common/financialEntities';
import {
    calculateForecastAmount,
    calculatePaymentAmount,
} from '@common/forecasting';
import {
    getAnnualReturnUsingRiskScore,
    getHistoricalReturn,
    Goal,
    GoalGroup,
} from '@common/goals';
import {
    CurrentRateInfo,
    GoalDocument,
    ShareStatus,
    UpdateGoalDocument,
    UpdateGoalMutationVariables,
} from '@common/graphql/generated';
import { useFinancialEntitiesContext } from '@common/react/financialEntities';
import {
    getAmountRecurrenceStr,
    getGoalBalanceForSwitcherType,
    getJointGoalAccount,
    getMoneyMarketRateStr,
    getMyGoalAccount,
    getMyGoalAccountOnboarding,
    UpsertMyGoalAccountOnboardingParams,
    useUpsertMyGoalAccountOnboarding,
} from '@common/react/goals';
import { IconName } from '@common/react/icons';
import { useCurrentUser, UserInfo } from '@common/react/user';
import {
    EarliestWithdrawal,
    GoalAccountInvestmentType,
    Horizon,
    Recurrence,
    TransactionSchedulePlan,
} from '@common/types';
import { sortBy } from '@common/util/arrays';
import { dt } from '@common/util/dateTime';
import { getRoundedUp } from '@common/util/math';
import { toPercentageStr } from '@common/util/numbers';
import { typedEntries } from '@common/util/objects';
import pluralize from '@common/util/pluralize';
import { getNextRecurrence } from '@common/util/recurrence';
import { isNotNullish } from '@common/util/types';
import { useEffect, useState } from 'react';
import { SliderConfig } from '../../common/FormattedInput';
import { Paragraph } from '../../common/Typography';
import { useMyDisconnectedAccounts } from '../../financialEntities/helpers';
import { GoalData, InputStepConfig } from '../config/types';
import { getActionableGoalAccountForCurrentUser } from '../detail/helpers';
import { showGoalDepositFlowModal } from '../GoalDepositFlowModal';
import ReturnDisclosureMMF from '../ReturnDisclosureMMF';
import ReturnDisclosurePortfolio from '../ReturnDisclosurePortfolio';
import {
    getPartnerGoalAccountData,
    PartnerGoalAccountData,
} from './configHelpers';

export type RiskLabel =
    | 'Least risky'
    | 'Less risky'
    | 'Balanced risk'
    | 'More risky';

export const riskLabelByRiskScore: Record<number, RiskLabel> = {
    0: 'Least risky',
    1: 'Less risky',
    2: 'Less risky',
    3: 'Less risky',
    4: 'Balanced risk',
    5: 'Balanced risk',
    6: 'Balanced risk',
    7: 'More risky',
    8: 'More risky',
    9: 'More risky',
    10: 'More risky',
};

export const iconByGoalGroup: Record<GoalGroup, IconName> = {
    HIGH_YIELD_CASH_ONLY: 'Shield01',
    INVEST: 'TrendUp01',
    TRACK: 'SearchMD',
};

export const labelByGoalGroup: Record<GoalGroup, string> = {
    HIGH_YIELD_CASH_ONLY: 'HIGH YIELD',
    INVEST: 'INVEST',
    TRACK: 'TRACK',
};

/**
 * Returns current T-bill rate as a percentage, rounded to two decimals
 *
 * Example: 5.18%
 */
export const getTbillRateStr = (currentRateInfo: CurrentRateInfo) => {
    return `${(currentRateInfo.tbillRate * 100).toFixed(2)}%`;
};

export const getHistoricalReturnStr = ({
    investmentType,
    riskScore,
    currentRateInfo,
}: {
    investmentType: GoalAccountInvestmentType;
    riskScore: number;
    currentRateInfo: CurrentRateInfo;
}): string => {
    const historicalReturn = getHistoricalReturn({
        investmentType,
        riskScore,
        currentRateInfo,
    });
    return Math.round(historicalReturn * 10000) / 100 + '%';
};

export const useUpdateGoal = (goal: Goal) => {
    const [updateGoal] = useMutation(UpdateGoalDocument);
    const upsertMyGoalAccountOnboarding =
        useUpsertMyGoalAccountOnboarding(goal);

    return async (
        goalParams: UpdateGoalMutationVariables,
        goalAccountOnboardingParams: UpsertMyGoalAccountOnboardingParams
    ) => {
        await updateGoal({ variables: goalParams });
        // TODO: Wedge this into the updateGoal mutation
        const res = await upsertMyGoalAccountOnboarding(
            goalAccountOnboardingParams
        );
        return res.data?.updateGoalAccountOnboarding?.goal;
    };
};

export const useGoal = (goalId: string | null | undefined) => {
    const { data, loading } = useQuery(
        GoalDocument,
        goalId ? { variables: { id: goalId } } : { skip: true }
    );
    return { goal: data?.goal as Goal | undefined, isLoading: loading };
};

/**
 * Get the investment horizon based on a target date:
 * - `SHORT_TERM` if less than 2 years from now.
 * - `MEDIUM_TERM` if 2-5 years from now.
 * - `LONG_TERM` if more than 5 years from now.
 */
const horizonFromTargetDate = (targetDate: string): Horizon => {
    const diffYears = dt(targetDate).diff(dt(), 'years');

    return diffYears < 2
        ? 'SHORT_TERM'
        : diffYears < 5
          ? 'MEDIUM_TERM'
          : 'LONG_TERM';
};

export const getAnnualReturnInfoForRiskScore = ({
    investmentType,
    riskScore,
    currentRateInfo,
}: {
    investmentType: GoalAccountInvestmentType;
    riskScore: number;
    currentRateInfo: CurrentRateInfo;
}): {
    annualReturnLabel: string;
    annualReturnLabelCapitalized: string;
    annualReturnStr: string;
    annualReturnDisclosure: JSX.Element;
} => {
    return investmentType === 'HIGH_YIELD_CASH'
        ? {
              annualReturnLabel: 'APY after fees',
              annualReturnLabelCapitalized: 'APY after fees',
              annualReturnStr: getMoneyMarketRateStr(currentRateInfo),
              annualReturnDisclosure: <ReturnDisclosureMMF />,
          }
        : {
              annualReturnLabel: 'hypothetical return',
              annualReturnLabelCapitalized: 'Hypothetical return',
              annualReturnStr: getHistoricalReturnStr({
                  investmentType,
                  riskScore,
                  currentRateInfo,
              }),
              annualReturnDisclosure: <ReturnDisclosurePortfolio />,
          };
};

export const getRecommendedRiskScoreUsingTargetDate = <TGoal extends Goal>({
    goalData,
    userInfo,
}: {
    goalData: GoalData<TGoal>;
    userInfo: UserInfo;
}): number | null => {
    const { recommendedRiskScoreByHorizon } = userInfo;
    const { targetDate } = goalData;

    // If target date is within the next 12 months, recommend risk score of 0
    if (targetDate && dt(targetDate).isBefore(dt().add(1, 'year'))) {
        return 0;
    }

    if (recommendedRiskScoreByHorizon && targetDate) {
        return recommendedRiskScoreByHorizon[horizonFromTargetDate(targetDate)];
    }

    return null;
};

export const getGoalCardIndexFromFieldName = <TGoal extends Goal>(
    inputSteps: InputStepConfig<TGoal>[],
    fieldName: string
): number | null => {
    // Find the index of the step containing the field
    const idx = inputSteps.findIndex((inputStep) =>
        inputStep.fieldConfigs.find(
            (fieldConfig) => fieldConfig.fieldName === fieldName
        )
    );
    if (idx === -1) return null;
    return idx;
};

export interface DeriveTransactionSchedulePlanInput {
    annualReturn: number;
    initialDepositPlan: number;
    transactionSchedulePlan?: TransactionSchedulePlan;
    /** For existing goal, pass goal */
    goal?: Goal;
    /** For new goal, or to override, pass targets explicitly */
    targetAmount?: number;
    targetDate?: string;
    /** To calculate partner return, if necessary, from goal */
    currentRateInfo: CurrentRateInfo;
}

export interface DeriveTransactionSchedulePlanOptions {
    resetStartDate?: boolean;
    clampToTargetAmount?: boolean;
}

/**
 * Workhorse function to derive a suitable transaction schedule plan given a
 * set of constraint inputs.
 *
 * Should always be used when determining a new or updated transaction schedule
 * plan to ensure this logic remains consistent throughout the UI.
 */
export const deriveTransactionSchedulePlan = (
    input: DeriveTransactionSchedulePlanInput,
    options?: DeriveTransactionSchedulePlanOptions
): TransactionSchedulePlan => {
    const currentDate = dt().toDateStr();
    const targetAmount = input.targetAmount ?? input.goal?.targetAmount;
    const targetDate = input.targetDate ?? input.goal?.targetDate;

    let partnerPlanInfo: PartnerGoalAccountData | undefined;
    if (input.goal) {
        partnerPlanInfo = getPartnerGoalAccountData(
            input.goal,
            input.currentRateInfo
        );
    }

    // Clamp to target amount if explicitly specified,
    // or if no transaction schedule was passed and we're constructing from scratch.
    const shouldClamp =
        options?.clampToTargetAmount ?? !input.transactionSchedulePlan;

    let derivedPlan: TransactionSchedulePlan = input.transactionSchedulePlan
        ? { ...input.transactionSchedulePlan }
        : partnerPlanInfo?.transactionSchedulePlans?.length
          ? partnerPlanInfo.transactionSchedulePlans[0]
          : {
                amount: 0,
                recurrence: { freq: 'MONTHLY', interval: 1 },
                startDate: currentDate,
            };

    // If transaction schedule plan is < current date,
    // or options.resetStartDate was passed,
    // set transaction schedule plan start date to current date.
    if (derivedPlan.startDate < currentDate || options?.resetStartDate) {
        derivedPlan = {
            ...derivedPlan,
            startDate: currentDate,
        };
    }

    // If initial deposit is specified, and transaction schedule plan would
    // start today, offset the transaction schedule start by one period.
    // This prevents double-deposits on day 1.
    if (input.initialDepositPlan && derivedPlan.startDate === currentDate) {
        const startDate = getNextRecurrence({
            dtStart: dt(derivedPlan.startDate),
            recurrence: derivedPlan.recurrence,
            dtOnOrAfter: dt(derivedPlan.startDate).add(1, 'day'),
        }).toDateStr();
        derivedPlan = {
            ...derivedPlan,
            startDate,
        };
    }

    // Start date was clamped to 28 in the previous function. However, if
    // the 28th is < current date, coerce to start of *next* month.
    // N.B. This is a known UX impairment. Consider supporting dates >28
    // holistically down the road.
    if (currentDate > derivedPlan.startDate) {
        const startDate = dt(derivedPlan.startDate)
            .add(1, 'month')
            .startOf('month')
            .toDateStr();
        derivedPlan = {
            ...derivedPlan,
            startDate,
        };
    }

    // If goal is evergreen or should not be clamped, just return plan as-is

    if (!targetAmount || !targetDate || !shouldClamp) return derivedPlan;

    // If goal is not evergreen and should be clamped, recalculate transaction
    // schedule plan.

    // If not a shared goal, just recalculate.
    if (input.goal?.shareStatus !== 'SHARED') {
        return calculateTransactionSchedulePlanForTarget({
            startDate: currentDate,
            startAmount: input.initialDepositPlan,
            targetDate,
            targetAmount,
            annualReturn: input.annualReturn,
            recurrence: derivedPlan.recurrence,
            recurrenceStartDate: derivedPlan.startDate,
        });
    }

    // If goal is shared,factor in partner's contribution.

    // If partner has already invested, calculate a complimentary plan
    if (partnerPlanInfo?.transactionSchedulePlans?.length) {
        return calculateComplimentaryTransactionSchedulePlan({
            existingPlan: {
                startAmount: partnerPlanInfo?.startAmount,
                annualReturn: partnerPlanInfo.annualReturn,
                transactionSchedulePlan:
                    partnerPlanInfo.transactionSchedulePlans[0],
            },
            annualReturn: input.annualReturn,
            startAmount: input.initialDepositPlan,
            startDate: dt().toDateStr(),
            targetAmount,
            targetDate,
        });
    }

    // Otherwise, return a plan with half of the "full" amount required
    // to hit the goal targets.

    const fullPlan = calculateTransactionSchedulePlanForTarget({
        startDate: currentDate,
        startAmount: input.initialDepositPlan,
        targetDate,
        targetAmount,
        // TODO: Consider both partners.
        // N.B. We're allowing this, for now, since we don't believe getting
        // this perfect at this stage is especially valuable to the user,
        // will likely be significantly confusing (why are you showing slightly
        // different values for me vs. my partner?),
        // and the optimization computation seems difficult/impossible to
        // achieve in constant time.
        annualReturn: input.annualReturn,
        recurrence: derivedPlan.recurrence,
        recurrenceStartDate: derivedPlan.startDate,
    });

    return {
        ...fullPlan,
        // half of target amount, rounded up to the nearest $1
        amount: getRoundedUp(fullPlan.amount / 2, 1_00),
    };
};

/**
 * Calculate estimated payment required to hit `targetAmount` by `targetDate`.
 */
export const calculateTransactionSchedulePlanForTarget = ({
    annualReturn,
    recurrence,
    recurrenceStartDate,
    targetAmount,
    targetDate,
    startDate,
    startAmount,
}: {
    annualReturn: number;
    recurrence: Recurrence;
    recurrenceStartDate?: string | null;
    startAmount?: number | null;
    startDate: string;
    targetAmount: number;
    targetDate: string;
}): TransactionSchedulePlan => {
    const amount = calculatePaymentAmount({
        startAmount,
        targetAmount,
        targetDate,
        startDate,
        annualGrowthRate: annualReturn,
        recurrence,
        recurrenceStartDate,
    });
    return {
        // prevent negative amounts and round up to nearest $1 increment
        amount: getRoundedUp(Math.max(0, amount), 1_00),
        recurrence,
        startDate: recurrenceStartDate ?? startDate,
    };
};

export const calculateComplimentaryTransactionSchedulePlan = ({
    existingPlan,
    annualReturn,
    startAmount,
    startDate,
    targetAmount,
    targetDate,
}: {
    existingPlan: {
        startAmount: number;
        annualReturn: number;
        transactionSchedulePlan?: TransactionSchedulePlan;
    };
    annualReturn: number;
    startAmount: number;
    startDate: string;
    targetAmount: number;
    targetDate: string;
}): TransactionSchedulePlan => {
    // Calculate amount that existing plan will contribute by targetDate.
    const existingContributionToTargetAmount = calculateForecastAmount({
        startDate,
        targetDate,
        startAmount: existingPlan.startAmount,
        transactionSchedulePlans: existingPlan.transactionSchedulePlan
            ? [existingPlan.transactionSchedulePlan]
            : [],
        annualGrowthRate: existingPlan.annualReturn,
    });

    // Calculate amount that this complimentary plan will need to provide by
    // targetDate, by subtracting the existing plan's contribution to the
    // target amount from the overall target amount.
    const complimentaryTargetAmount = Math.max(
        targetAmount - existingContributionToTargetAmount,
        0
    );

    const complimentaryTransactionSchedulePlan =
        calculateTransactionSchedulePlanForTarget({
            annualReturn,
            recurrence: existingPlan.transactionSchedulePlan?.recurrence ?? {
                freq: 'MONTHLY',
                interval: 1,
            },
            recurrenceStartDate:
                existingPlan.transactionSchedulePlan?.startDate,
            startAmount,
            startDate,
            targetDate,
            targetAmount: complimentaryTargetAmount,
        });

    return complimentaryTransactionSchedulePlan;
};

export const getFinalAssumptionsWithoutRisk = <TGoal extends Goal>({
    goalData,
    currentRateInfo,
}: {
    goalData: GoalData<TGoal>;
    currentRateInfo: CurrentRateInfo;
}) => {
    const { transactionSchedulePlan } = goalData;
    return (
        <>
            <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                {'Your hypothetical amount is based on the following:'}
            </Paragraph>
            <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                {transactionSchedulePlan && (
                    <ListItem>{`${getAmountRecurrenceStr(
                        transactionSchedulePlan
                    )} contributions are consistent`}</ListItem>
                )}
                <ListItem>Interest earned is automatically reinvested</ListItem>
                <ListItem>
                    {getMoneyMarketRateStr(currentRateInfo)} included is
                    inclusive of advisory fees
                </ListItem>
            </UnorderedList>
            <Paragraph mt={4} fontSize="sm" lineHeight="tall">
                The impact of taxes isn’t included, and things like federal
                interest rate changes, and making regular contributions (like
                recurring deposits) can affect your outcome. Plus, as the
                federal interest rate goes up, this money market fund rate may
                go up and you could earn even more (it may also go down and you
                would earn less).
            </Paragraph>
        </>
    );
};

export const getFinalAssumptionsWithRisk = <TGoal extends Goal>({
    goalData,
    recommendedRiskScore,
    currentRateInfo,
}: {
    goalData: GoalData<TGoal>;
    recommendedRiskScore: number | null;
    currentRateInfo: CurrentRateInfo;
}) => {
    const { riskScore, transactionSchedulePlan } = goalData;
    const amountRecurrenceStr = transactionSchedulePlan
        ? getAmountRecurrenceStr(transactionSchedulePlan)
        : undefined;
    const historicalReturn = getAnnualReturnUsingRiskScore({
        investmentType:
            (riskScore ?? recommendedRiskScore) === 0
                ? 'HIGH_YIELD_CASH'
                : goalData.investmentType,
        riskScore: riskScore ?? recommendedRiskScore,
        currentRateInfo,
    });

    return (
        <>
            <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                {'Your hypothetical amount is based on the following:'}
            </Paragraph>
            <UnorderedList fontSize="sm" lineHeight="tall" spacing={1}>
                {amountRecurrenceStr && (
                    <ListItem>{`${amountRecurrenceStr} contributions are consistent`}</ListItem>
                )}
                <ListItem>{`The risk level is consistent (which impacts the hypothetical return rate of ${toPercentageStr(historicalReturn)})`}</ListItem>
                <ListItem>{`Dividends and interest earned are automatically reinvested`}</ListItem>
            </UnorderedList>
            <Paragraph mt={4} fontSize="sm" lineHeight="tall">
                {`The impact of taxes are not included, and actual returns may differ. Things like market performance, risk tolerance, and making regular contributions (like recurring deposits) can affect outcomes.`}
            </Paragraph>
        </>
    );
};

export const getIOrWe = ({ shareStatus }: { shareStatus: ShareStatus }) =>
    shareStatus === 'SHARED' ? 'we' : 'I';

export const getGoalDataFromExistingGoal = <TGoal extends Goal>(
    goal: TGoal
): GoalData<TGoal> => {
    const myOnboarding = getMyGoalAccountOnboarding(goal);

    return {
        ...goal.typeInfo,
        ...(myOnboarding?.info ?? { infoType: 'DEFAULT' }),
        name: goal.name,
        shareStatus: goal.shareStatus,
        isMine: goal.isMine,
        balance: getGoalBalanceForSwitcherType(goal, 'SHARED') ?? 0,
        targetAmount: goal.targetAmount,
        targetDate: goal.targetDate,
    };
};

export const iconByShareStatus: Record<ShareStatus, IconName> = {
    PRIVATE: 'Lock01',
    SHARED: 'OverlappingCircles',
};

/**
 * Returns sentence fragment describing the number of months
 *
 * Example: 1 month
 *
 * Example: 2 months
 */
export const getMonthsStr = (months: number) =>
    pluralize('month', months, true);

export const getTargetDateFromYearMonth = (yearMonth: string): string =>
    `${yearMonth.slice(0, 4)}-${yearMonth.slice(4)}-01`;

export const getYearMonthFromTargetDate = (targetDate: string): string =>
    `${targetDate.slice(0, 4)}${targetDate.slice(5, 7)}`;

export const getTargetDateFromStartDateAndYears = (
    startDate: string,
    inNumYears?: number
): string | undefined => {
    if (!inNumYears) return;
    // Use end of month to align with chart forecasting logic assumptions.
    return dt(startDate).add(inNumYears, 'years').endOf('month').toDateStr();
};

export const labelByEarliestWithdrawal: Record<EarliestWithdrawal, string> = {
    '0_TO_2_YEARS': '0 - 2 years',
    '2_TO_5_YEARS': '2 - 5 years',
    '5_PLUS_YEARS': '5+ years',
};

export const maxYearsByEarliestWithdrawal: Record<
    Exclude<EarliestWithdrawal, '5_PLUS_YEARS'>,
    number
> = {
    '0_TO_2_YEARS': 2,
    '2_TO_5_YEARS': 5,
};

export const horizonByEarliestWithdrawal: Record<EarliestWithdrawal, Horizon> =
    {
        '0_TO_2_YEARS': 'SHORT_TERM',
        '2_TO_5_YEARS': 'MEDIUM_TERM',
        '5_PLUS_YEARS': 'LONG_TERM',
    };

export const earliestWithdrawalOptions: {
    value: EarliestWithdrawal;
    label: string;
}[] = typedEntries(labelByEarliestWithdrawal).map(([value, label]) => ({
    value,
    label,
}));

export const getInNumYears = ({
    age,
    earliestWithdrawal,
}: {
    age: number;
    earliestWithdrawal: EarliestWithdrawal;
}): { inNumYears: number; inNumYearsStr: string } => {
    let inNumYears: number;
    let inNumYearsStr: string;
    if (earliestWithdrawal === '5_PLUS_YEARS') {
        // If they select 5+ years, we calculate the amount by the time they’re
        // 65 years old. If they're >= 55, we just use 10 years.
        inNumYears = 65 - age;
        inNumYearsStr = "when I'm 65";
        if (age >= 55) {
            inNumYears = 10;
            inNumYearsStr = `in ${inNumYears} year${
                inNumYears === 1 ? '' : 's'
            }`;
        }
    } else {
        inNumYears = maxYearsByEarliestWithdrawal[earliestWithdrawal];
        inNumYearsStr = `in ${inNumYears} year${inNumYears === 1 ? '' : 's'}`;
    }

    return { inNumYears, inNumYearsStr };
};

/**
 * Returns sentence describing the age of user on the supplied date
 *
 * Example: You will be 39 years old.
 */
export const getYearsOldStr = ({
    date,
    userInfo,
}: {
    date: string;
    userInfo: UserInfo;
}) => {
    if (!userInfo.dob) return;
    // Don't show age string if target date is less than 12 months.
    if (dt(date).diff(dt(), 'months') < 12) return;

    const age = dt(date).diff(dt(userInfo.dob), 'years');
    return `You’ll be ${age} years old.`;
};

/**
 * Simply sets min=0, max= target-start amount if provided (for targeted
 * goals), otherwise uses 10k (for untargeted goals), with a nonlinear scale.
 * Setting max=target-start ensures user can always "pay for all in one period";
 * while unlikely to be desired in practice, we received user feedback that the
 * interaction felt weird without this full range available.
 */
export const getGoalTransactionScheduleInputSliderConfig = ({
    targetAmount,
    startAmount,
}: {
    targetAmount?: number | null;
    startAmount?: number | null;
}): SliderConfig => {
    const max = targetAmount ? targetAmount - (startAmount ?? 0) : 10_000_00;

    return {
        min: 0,
        max,
        step: 10_00, // Unused, since nonlinear=true. TODO: Refine prop typedefs.
        nonlinear: true,
    };
};

export const useInitialDepositPlanSliderConfig = ({
    initialDepositPlan,
    targetAmount,
}: {
    initialDepositPlan: number;
    targetAmount?: number | null;
}): SliderConfig => {
    const DEFAULT_MAX = 10_000_00; // $10,000
    const [max, setMax] = useState(targetAmount ?? DEFAULT_MAX);

    useEffect(() => {
        // If goal has target amount, keep slider max equal to target amount.
        if (targetAmount) return;

        if (initialDepositPlan > max) {
            // If user has entered an amount greater than the slider max,
            // set the slider max to double the entered amount.
            setMax(initialDepositPlan * 2);
        } else if (initialDepositPlan === 0) {
            // If user changes initial deposit plan to $0 (perhaps because the
            // slider max became way too large), reset slider max to default
            setMax(DEFAULT_MAX);
        }
    }, [targetAmount, initialDepositPlan, max]);

    return {
        min: 0,
        max,
        step:
            // 0-10k? step is 100
            max <= 10_000_00
                ? 100_00
                : // 10k-100k? step is 1k
                  max <= 100_000_00
                  ? 1_000_00
                  : // otherwise step is 10k
                    10_000_00,
    };
};

export const useTransferDestinationGoals = ({
    sourceGoal,
}: {
    sourceGoal: Goal;
}): {
    isLoading: boolean;
    goals: Goal[];
} => {
    const { fes } = useFinancialEntitiesContext();
    const goals = fes.data?.goals;
    if (!goals) return { isLoading: true, goals: [] };

    const mySourceGoalAccount =
        getMyGoalAccount(sourceGoal) ?? getJointGoalAccount(sourceGoal);

    assert(mySourceGoalAccount);

    const sortedGoals = sortBy(
        goals
            // only show goals that are of the same account type as the source goal
            .filter((goal) => {
                const myGoalAccount =
                    getMyGoalAccount(goal) ?? getJointGoalAccount(goal);

                return (
                    myGoalAccount?.info?.accountType ===
                        mySourceGoalAccount.info.accountType &&
                    goal.id !== sourceGoal.id
                );
            }),
        (g) => g.createdAt,
        'DESC'
    );

    return { isLoading: false, goals: sortedGoals };
};

/**
 * Encapsulates logic for what to use as the "starting amount" in goal
 * forecasting calculations:
 * If a balance exists, use that; otherwise, use the initialDepositPlan.
 * FIXME: This does not handle for the situation where a user funded & then
 * withdrew all from the goal account.
 */
export const getGoalForecastStartAmount = (
    balance: number | null | undefined,
    initialDepositPlan: number | null | undefined
): number => {
    return balance || (initialDepositPlan ?? 0);
};

export const goalGuidanceCardWidth = 398;
export const goalGuidanceCardHeight = 476;

export const useMyDisconnectedFundingAccountsForGoalAccount = (
    goalAccount: PlentyGoalAccountFinancialEntity | undefined
) => {
    const disconnectedAccounts = useMyDisconnectedAccounts();

    if (!goalAccount) return [];

    const transactionSchedules =
        activeTransactionSchedulesFromGoalAccount(goalAccount);

    const transactionScheduleAccountIds = new Set(
        transactionSchedules.flatMap((ts) =>
            [ts.sourceId, ts.destinationId].filter(isNotNullish)
        )
    );
    const disconnectedFundingAccounts = disconnectedAccounts.filter((fe) =>
        transactionScheduleAccountIds.has(fe.id)
    );

    return disconnectedFundingAccounts;
};

export const useStartGoalDepositFlow = (goal?: Goal | null | undefined) => {
    const { featureFlags } = useCurrentUser();

    if (featureFlags.isShutdownV1Enabled) return;

    const isMoneyMovementEnabled = featureFlags.isMoneyMovementEnabled;
    if (!isMoneyMovementEnabled || !goal) return;

    const goalAccount = getActionableGoalAccountForCurrentUser(goal);
    if (!goalAccount) return;

    return () => {
        showGoalDepositFlowModal({
            goalId: goal.id,
            destinationId: goalAccount.id,
        });
    };
};

export const showCompleteSetupOnMobileModal = () =>
    showInfoModal({
        title: "🔧 We're upgrading investing",
        children: (
            <Paragraph>
                For now, goals created through the mobile app need to be
                finished on the mobile app. You’re also able to create new goals
                on the web app.
            </Paragraph>
        ),
    });
